import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Form,
	Header,
	SpaceBetween,
	Alert
} from '@cloudscape-design/components'
import { FC, ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import FormSelect from '../../../../../components/ControlledFormInputs/Select'
import { useForm } from 'react-hook-form'
import { useNewSslCertsFormStore } from '../../../../../stores/projects/projectResource/sslCerts/newSslCertsFormStore'
import FormInput from '../../../../../components/ControlledFormInputs/Input'
import useSingleProject from '../../../../../hooks/query/useSingleProject'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import getAllProjectResources from '../../../../../api/projects/resources/getAllProjectResources'
import {
	getProjectResourceApprovalObject,
	ProjectResourceRequest
} from '../../../../../types/projectResource'
import { useOktaAuth } from '@okta/okta-react'
import useCreateNewProjectResource from '../../../../../hooks/mutate/useCreateNewProjectResource'
import formatCertificateName from '../../../../../helper/ProjectResources/SSLCerts/formatCertificateName'
import CONSTANTS from '../../../../../constants'

type FormInputContainerProps = {
	children: ReactNode
}

function FormInputContainer(props: FormInputContainerProps) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
			<div style={{ width: '650px' }}>{props.children}</div>
		</div>
	)
}

const NewSslCerts: FC = () => {
	const { id: projectId } = useParams() as { id: string }

	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const navigate = useNavigate()

	const { data: project, isLoading } = useSingleProject(projectId)

	const [existingResources, setExistingResources] = useState<any[]>([])

	const [isDuplicateError, setIsDuplicateError] = useState(false)

	const [isApprovedDuplicate, setIsApprovedDuplicate] = useState(false)

	const [isDualProvisioning, setIsDualProvisioning] = useState(false)

  const [dualProvisioningWarningMessage, setDualProvisioningWarningMessage] =
    useState<{ header: string, message: string }>({
    header: "", message: "Requesting this will reprovision existing certificates."
  })

  const [certs, setCerts] = useState<Array<string>>([])

	const { mutate: createResource, isLoading: isSendLoading } =
		useCreateNewProjectResource()

	const {
		formValues: newSslCertFormValues,
		actions: { setFormValues: setNewSslCertFormValues, clearFormValues }
	} = useNewSslCertsFormStore()

	const {
		control,
		setValue,
		trigger,
		setError,
		formState: { isValid }
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: newSslCertFormValues
	})

	const [domainNameFieldRules, setDomainNameFieldRules] = useState({})

	const checkDualProvision = () => {
  	let dualProvisioningWarn = false
  	let message: string = ""
   	// Check for existing 'both' request
  	if (newSslCertFormValues.certType !== 'both')
  	{
  	  existingResources.forEach(
  			resource => {
    		  if (resource.details?.certType === 'both') {
    				dualProvisioningWarn = true
            message = "Existing domain & wildcard certificates provisioned"
   			} else {
            dualProvisioningWarn = false
          }
  		})
  	}
  	else // Check for existing single type request
  	{
        existingResources.forEach(resource => {
          if (resource.details?.certType !== 'both') { // Either 'domain' or 'wildcard'
            dualProvisioningWarn = true
            message = `Existing ${resource.details?.certType} certificate provisioned`
          } else {
            dualProvisioningWarn = false
          }
        })
  	}

  	setIsDualProvisioning(dualProvisioningWarn)
    setDualProvisioningWarningMessage({...dualProvisioningWarningMessage, header: message})
  }

	useEffect(() => {
		setNewSslCertFormValues('projectID', projectId)
		setNewSslCertFormValues(
			'accountDistributionList',
			project?.details.distributionList
		)
		setNewSslCertFormValues('hostedZoneName', project?.approval?.details.dnsZoneApex)
		setValue('hostedZoneName', project?.approval?.details.dnsZoneApex)
		setNewSslCertFormValues('projectName', project?.projectName)
		setValue('certType', CONSTANTS.CERT_TYPE_OPTIONS[0])
	}, [projectId, project, setNewSslCertFormValues, setValue])

	useEffect(() => {
		checkDualProvision()
		if (newSslCertFormValues.certType === 'domain' || newSslCertFormValues.certType === 'both') {
			setValue('domainName', '')
			setNewSslCertFormValues('domainName', '')
			// Commented because domain name field is currently disabled
			// setDomainNameFieldRules({
			// 	pattern: {
			// 		message: 'Should be lowercase letters and digits.',
			// 		value: /([a-z-0-9])/
			// 	}
			// })
		} else if (newSslCertFormValues.certType === 'wildcard') {
			setValue('domainName', '*')
			setNewSslCertFormValues('domainName', '*')
			setDomainNameFieldRules({
				pattern: {
					message: 'Asterisk only allowed',
					value: /\*/
				}
			})
		}

		trigger()
	}, [newSslCertFormValues.certType, setNewSslCertFormValues, setValue, trigger])

	useEffect(() => {
		const fetchProjectResources = async () => {
			try {
				const result = await getAllProjectResources('SSL_CERT')
				const projectResources = result.body

				const thisProjectResources = projectResources.filter(
					(resource) =>
						resource.projectID === projectId &&
						resource.details?.projectName === project?.details?.projectName &&
						(resource.status === 'PENDING_APPROVAL' || resource.status === 'APPROVED')
				)

				setExistingResources(thisProjectResources)
			} catch (error) {
				console.error('Error fetching project resources:', error)
			}
		}
		if (project) {
			fetchProjectResources()
		}
	}, [projectId, project])

	useEffect(() => {
		if (newSslCertFormValues.domainName || newSslCertFormValues.hostedZoneName) {
			checkForDuplicate()
		}
	}, [newSslCertFormValues.domainName, newSslCertFormValues.hostedZoneName, existingResources, checkDualProvision])

	// Set display of certs
	useEffect(() => {
	  if (newSslCertFormValues.certType !== 'both') {
			setCerts([formatCertificateName(newSslCertFormValues.domainName, newSslCertFormValues.hostedZoneName, newSslCertFormValues.certType)])
		}
		else {
		  setCerts(
				[
				    formatCertificateName('', newSslCertFormValues.hostedZoneName, 'domain'),
						formatCertificateName('*', newSslCertFormValues.hostedZoneName, 'wildcard')
				]
			)
		}
	}
	,[newSslCertFormValues])

	if (isLoading) {
		return <LoadingSpinner />
	}

	if (project === undefined) {
		return (
			<>
				<h1>Project Not Found</h1>
			</>
		)
	}

	const checkForDuplicate = () => {

		const domain = newSslCertFormValues.domainName
		const hostedZone = newSslCertFormValues.hostedZoneName

		let foundPending = false
		let foundApproved = false
		let dualProvisioningWarn = false
		let message: string = ""

		existingResources.forEach(resource => {
			if (
				resource.details?.domainName === domain &&
				resource.details?.hostedZoneName === hostedZone
			)
			{
				if (resource.status === 'PENDING_APPROVAL') {
					foundPending = true
				}
				if (resource.status === 'APPROVED') {
					foundApproved = true
				}
			}
		})

		setIsDuplicateError(foundPending)
		setIsApprovedDuplicate(!foundPending && foundApproved)
	}

	const handleSubmit = async () => {
		await trigger()

		if (!isValid) {
			return false
		}

		let newProjectResource: ProjectResourceRequest = {
			status: 'PENDING_APPROVAL',
			requestorID: thisUserEmail,
			details: newSslCertFormValues,
			action: 'CREATE',
			resourceType: 'SSL_CERT',
			approvalStatus: getProjectResourceApprovalObject(['ISDP'])
		}

		createResource(newProjectResource, {
			onSuccess: () => {
				clearFormValues()
				navigate(`/projects/${projectId}/sslCerts`)
			}
		})
	}

	return (
		<Form
			header={
				<Header variant="h1">
					New SSL Certificate for {project.details?.projectName}
				</Header>
			}
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Box>
						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={isSendLoading}
							disabled={!isValid || isSendLoading || isDuplicateError}
						>
							Submit
						</Button>
						<Button
							variant="link"
							onClick={() => {
								navigate(`/projects/${projectId}/sslCerts`)
								clearFormValues()
							}}
							disabled={isSendLoading}
						>
							Cancel
						</Button>
					</Box>
				</SpaceBetween>
			}
		>
			<SpaceBetween direction="vertical" size="s">
				{isDuplicateError && (
					<Alert
						type="error"
						header="Duplicate Certificate Request"
					>
						A certificate request with the same Domain Name and Hosted Zone is already pending approval.
					</Alert>
				)}

				{isApprovedDuplicate && (
					<Alert
						type="info"
						header="Certificate Already Approved"
					>
						A certificate for the same Domain Name and Hosted Zone has already been approved and created.
					</Alert>
				)}

				{isDualProvisioning && (
  				<Alert
  					type="warning"
  					header={dualProvisioningWarningMessage.header}
  				>
            {dualProvisioningWarningMessage.message}
  				</Alert>
				)}
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<FormInputContainer>
							<FormSelect
								control={control}
								setFormValues={setNewSslCertFormValues}
								name="certType"
								label="Certificate Type"
								placeholder="Select Certificate Type"
								options={CONSTANTS.CERT_TYPE_OPTIONS}
								rules={{
									required: 'Please select a Certificate Type'
								}}
							/>
						</FormInputContainer>

						<ColumnLayout columns={2}>
							<FormInputContainer>
								<FormInput
									control={control}
									setFormValues={setNewSslCertFormValues}
									name="domainName"
									label="Domain Name"
									placeholder="Enter Domain Name"
									disabled
									rules={domainNameFieldRules}
								/>
							</FormInputContainer>

							<FormInputContainer>
								<FormInput
									control={control}
									setFormValues={setNewSslCertFormValues}
									name="hostedZoneName"
									label="Hosted Zone Apex Name"
									disabled
								/>
							</FormInputContainer>
						</ColumnLayout>
					</SpaceBetween>
				</Container>
				{!(isDuplicateError || isApprovedDuplicate) && (
				<Container>
          <Header variant="h3">Certificates to be provisioned</Header>
          {certs.map((cert,key) => (
            <Box key={key}>{cert}</Box>
          ))}
				</Container>
				)}
			</SpaceBetween>
		</Form>
	)
}

export default NewSslCerts
